import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Advanced, Badge, Btn } from "./common";
import { CalendarCheck, Link2, RefreshCw } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/* Calendar authorisation (read + CONFIRMED event write). Separate consent upgrade on the same
   Google connection; nothing is claimed as connected until Google really granted the scopes. */
export const CalendarConnectionCard = ({ onChanged }) => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/calendar/status"); setS(r.data); onChanged?.(r.data); }
    catch { setS({ google_connected: false }); }
  }, [onChanged]);
  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gmail/connect", null, { params: { mode: "calendar" } });
      window.location.href = r.data.auth_url;
    } catch (e) { toast.error(e?.response?.data?.detail || tx("Yetkilendirme başlatılamadı")); setBusy(false); }
  };
  const pick = async (id) => {
    try { await api.put("/calendar/selected", { calendar_id: id }); toast.success(tx("Takvim seçildi")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || tx("Takvim seçilemedi")); }
  };

  if (!s) return null;
  const ready = !!s.can_write_events;
  return (
    <div data-testid="calendar-connection-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <Badge color={ready ? "green" : s.google_connected ? "amber" : "gray"} testid="calendar-connection-badge">
          {ready ? tx("Bağlı") : s.google_connected ? tx("Yetki verilmedi") : tx("Google bağlı değil")}
        </Badge>
        <div className="flex gap-2">
          {ready
            ? <Btn variant="ghost" onClick={load} data-testid="calendar-refresh-btn"><RefreshCw size={13} className="inline mr-1" />{tx("Yenile")}</Btn>
            : <Btn onClick={enable} disabled={busy || !s.google_connected} data-testid="calendar-enable-btn">
                <Link2 size={13} className="inline mr-1" />{busy ? tx("Yönlendiriliyor…") : tx("Takvimi etkinleştir")}
              </Btn>}
        </div>
      </div>

      <div className="text-sm text-white/75">
        {ready
          ? <>{tx("Okuma ve onaylı düzenleme açık")} · {s.timezone}</>
          : <>{tx("Takvim okuma ve onaylı etkinlik düzenleme için ayrı Google izni gerekir. Takvim silme, paylaşım ve ACL izinleri istenmez.")}</>}
      </div>

      {ready && (
        <label className="block mt-3">
          <span className="label-eyebrow block mb-1.5">{tx("Kullanılacak takvim")}</span>
          <select value={s.selected_calendar} onChange={(e) => pick(e.target.value)} data-testid="calendar-select"
            className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60">
            {(s.calendars || []).length === 0 && <option value={s.selected_calendar}>{s.selected_calendar}</option>}
            {(s.calendars || []).map((c) => (
              <option key={c.id} value={c.id} className="bg-[#0C1222]">{c.summary}{c.primary ? ` (${tx("birincil")})` : ""}</option>
            ))}
          </select>
        </label>
      )}

      <Advanced label={tx("İzinler ve ayrıntılar")} testid="calendar-advanced">
        <div>{tx("Etkinlik oluşturma/düzenleme yalnızca ekranda inceleyip onayladığınızda yapılır; asistan tek başına yazamaz.")}</div>
        {s.calendars_error && <div className="text-amber-200/90">{s.calendars_error}</div>}
      </Advanced>
    </div>
  );
};

export const CalendarConnectionIcon = CalendarCheck;
export default CalendarConnectionCard;
